import React, { useState, useEffect } from 'react';
import { ExclamationCircleIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';

interface Recebimento {
  id: number;
  numero: string;
  cliente: string;
  cliente_id: number | null;
  categoria: string;
  valor: number;
  data_pagamento: string;
  conta_bancaria: string;
}

interface Pagamento {
  id: number;
  numero: string;
  fornecedor: string;
  fornecedor_id: number | null;
  tipo_lancamento: 'F' | 'T';
  categoria: string;
  valor: number;
  data_pagamento: string;
  conta_bancaria: string;
}

interface MovimentacoesData {
  periodo: {
    data_inicio: string;
    data_fim: string;
  };
  resumo: {
    total_recebido: number;
    total_pago: number;
    saldo_periodo: number;
    saldo_bancario: number;
    qtd_recebimentos: number;
    qtd_pagamentos: number;
  };
  recebimentos: Recebimento[];
  pagamentos: Pagamento[];
}

const MovimentacoesReport: React.FC = () => {
  const [data, setData] = useState<MovimentacoesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const [dataInicio, setDataInicio] = useState(primeiroDia.toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(ultimoDia.toISOString().split('T')[0]);

  useEffect(() => {
    loadMovimentacoes();
  }, []);

  const loadMovimentacoes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeiroApi.getRelatorioMovimentacoes(dataInicio, dataFim);
      setData(response);
    } catch (err) {
      setError('Erro ao carregar movimentações');
      console.error('Error loading movimentacoes:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('pt-BR').format(new Date(dateString + 'T00:00:00'));
  };

  const handleFiltrar = () => {
    loadMovimentacoes();
  };

  const loadWithDates = async (inicio: string, fim: string) => {
    setDataInicio(inicio);
    setDataFim(fim);
    try {
      setLoading(true);
      setError(null);
      const response = await financeiroApi.getRelatorioMovimentacoes(inicio, fim);
      setData(response);
    } catch (err) {
      setError('Erro ao carregar movimentações');
      console.error('Error loading movimentacoes:', err);
    } finally {
      setLoading(false);
    }
  };

  const setPeriodoMesAtual = () => {
    const hoje = new Date();
    const primeiro = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimo = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    loadWithDates(primeiro.toISOString().split('T')[0], ultimo.toISOString().split('T')[0]);
  };

  const setPeriodoMesAnterior = () => {
    const hoje = new Date();
    const primeiro = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ultimo = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    loadWithDates(primeiro.toISOString().split('T')[0], ultimo.toISOString().split('T')[0]);
  };

  const setPeriodoAnoAtual = () => {
    const hoje = new Date();
    const primeiro = new Date(hoje.getFullYear(), 0, 1);
    const ultimo = new Date(hoje.getFullYear(), 11, 31);
    loadWithDates(primeiro.toISOString().split('T')[0], ultimo.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <h1 className="text-base font-bold text-secondary-900">Movimentações</h1>
        <p className="text-xs text-secondary-600 mt-0.5">Recebimentos e pagamentos efetivados</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-2 py-1.5 rounded text-xs flex items-center gap-2">
          <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="card">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-secondary-700 mb-0.5">Início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="input-field text-xs py-1 w-32"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-700 mb-0.5">Fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="input-field text-xs py-1 w-32"
            />
          </div>
          <button
            onClick={handleFiltrar}
            disabled={loading}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {loading ? '...' : 'Filtrar'}
          </button>
          <div className="flex flex-wrap gap-1 ml-auto">
            <button onClick={setPeriodoMesAtual} className="px-2 py-1 text-xs rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200">Mês</button>
            <button onClick={setPeriodoMesAnterior} className="px-2 py-1 text-xs rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200">Anterior</button>
            <button onClick={setPeriodoAnoAtual} className="px-2 py-1 text-xs rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200">Ano</button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Saldo Bancário */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded px-3 py-2 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-4 w-4" />
              <span className="text-xs font-medium">Saldo Bancário</span>
            </div>
            <div className="text-base font-bold">{formatCurrency(data.resumo.saldo_bancario)}</div>
          </div>

          {/* Cards de Resumo */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white border border-secondary-200 rounded px-2 py-1.5">
              <div className="text-xs text-secondary-500">Recebido</div>
              <div className="text-sm font-semibold text-green-600">{formatCurrency(data.resumo.total_recebido)}</div>
            </div>
            <div className="bg-white border border-secondary-200 rounded px-2 py-1.5">
              <div className="text-xs text-secondary-500">Pago</div>
              <div className="text-sm font-semibold text-red-600">{formatCurrency(data.resumo.total_pago)}</div>
            </div>
            <div className="bg-white border border-secondary-200 rounded px-2 py-1.5">
              <div className="text-xs text-secondary-500">Saldo</div>
              <div className={`text-sm font-semibold ${data.resumo.saldo_periodo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(data.resumo.saldo_periodo)}
              </div>
            </div>
          </div>

          {/* Recebimentos */}
          <div className="bg-white rounded border border-secondary-200 overflow-hidden">
            <div className="px-2 py-1 border-b border-secondary-200 bg-green-50">
              <span className="text-xs font-semibold text-green-800">Recebimentos ({data.resumo.qtd_recebimentos})</span>
            </div>
            {data.recebimentos.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500">Data</th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500">Cliente</th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 hidden sm:table-cell">Categoria</th>
                      <th className="px-2 py-1 text-right text-xs font-medium text-secondary-500">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {data.recebimentos.map((item) => (
                      <tr key={item.id} className="hover:bg-secondary-25">
                        <td className="px-2 py-1 text-xs text-secondary-900 whitespace-nowrap">{formatDate(item.data_pagamento)}</td>
                        <td className="px-2 py-1 text-xs text-secondary-900 truncate max-w-[120px] sm:max-w-[200px]" title={item.cliente}>{item.cliente}</td>
                        <td className="px-2 py-1 text-xs text-secondary-600 hidden sm:table-cell">{item.categoria}</td>
                        <td className="px-2 py-1 text-xs text-right font-medium text-green-600 whitespace-nowrap">{formatCurrency(item.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-green-50">
                    <tr>
                      <td colSpan={2} className="px-2 py-1 text-xs font-semibold text-green-800">TOTAL</td>
                      <td className="px-2 py-1 hidden sm:table-cell"></td>
                      <td className="px-2 py-1 text-right text-xs font-semibold text-green-800">{formatCurrency(data.resumo.total_recebido)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-3 text-xs text-secondary-500">Nenhum recebimento</div>
            )}
          </div>

          {/* Pagamentos */}
          <div className="bg-white rounded border border-secondary-200 overflow-hidden">
            <div className="px-2 py-1 border-b border-secondary-200 bg-red-50">
              <span className="text-xs font-semibold text-red-800">Pagamentos ({data.resumo.qtd_pagamentos})</span>
            </div>
            {data.pagamentos.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500">Data</th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500">Fornecedor</th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 hidden sm:table-cell">Categoria</th>
                      <th className="px-2 py-1 text-right text-xs font-medium text-secondary-500">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {data.pagamentos.map((item) => (
                      <tr key={item.id} className="hover:bg-secondary-25">
                        <td className="px-2 py-1 text-xs text-secondary-900 whitespace-nowrap">{formatDate(item.data_pagamento)}</td>
                        <td className="px-2 py-1 text-xs truncate max-w-[120px] sm:max-w-[200px]" title={item.fornecedor}>
                          <span className={item.tipo_lancamento === 'T' ? 'text-purple-700 font-medium' : 'text-secondary-900'}>
                            {item.fornecedor}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-xs text-secondary-600 hidden sm:table-cell">{item.categoria}</td>
                        <td className="px-2 py-1 text-xs text-right font-medium text-red-600 whitespace-nowrap">{formatCurrency(item.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-red-50">
                    <tr>
                      <td colSpan={2} className="px-2 py-1 text-xs font-semibold text-red-800">TOTAL</td>
                      <td className="px-2 py-1 hidden sm:table-cell"></td>
                      <td className="px-2 py-1 text-right text-xs font-semibold text-red-800">{formatCurrency(data.resumo.total_pago)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-3 text-xs text-secondary-500">Nenhum pagamento</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MovimentacoesReport;
